import React from 'react';
import { theme } from '../../theme';
import styled from '@emotion/styled';

export type ButtonGroupItem = {
  item: React.ReactNode;
  color?: 'default' | 'highlighted' | 'danger';
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  dataIcom?: string;
};

export type ButtonGroupProps = {
  size?: 'small' | 'medium';
  buttonGroupItems: ButtonGroupItem[];
  className?: string;
  style?: React.CSSProperties;
  isVertical?: boolean;
};

export const ButtonGroup = ({
  size = 'medium',
  buttonGroupItems,
  className,
  style,
  isVertical = false,
}: ButtonGroupProps) => {
  return (
    <Container className={className} style={style} isVertical={isVertical}>
      {buttonGroupItems.map((groupItem, index) => (
        <StyledGroupItem
          key={index}
          size={size}
          color={groupItem.color || 'default'}
          onClick={groupItem.onClick}
          type="button"
          disabled={groupItem.disabled || false}
          className={groupItem.className}
          style={groupItem.style}
          data-icom={groupItem.dataIcom}
        >
          {groupItem.item}
        </StyledGroupItem>
      ))}
    </Container>
  );
};

const Container = styled.div<{ isVertical?: boolean }>`
  display: inline-flex;
  border-radius: ${theme.borderRadius.l};
  border: 1px solid ${theme.colors.borderMain.default};
  background-color: ${theme.colors.backgroundCover.default};
  overflow: hidden;
  flex-direction: ${({ isVertical }) => (isVertical ? 'column' : 'row')};
`;

const StyledGroupItem = styled.button<{
  size?: 'small' | 'medium';
  color?: 'default' | 'highlighted' | 'danger';
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  'data-icom'?: string;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  font-family: ${({ size }) =>
    size === 'small' ? theme.typography.body.small.button.family : theme.typography.body.medium.button.family};
  font-weight: ${({ size }) =>
    size === 'small' ? theme.typography.body.small.button.fontWeight : theme.typography.body.medium.button.fontWeight};
  font-size: ${({ size }) =>
    size === 'small' ? theme.typography.body.small.button.fontSize : theme.typography.body.medium.button.fontSize};
  line-height: ${({ size }) => (size === 'small' ? '16px' : '22px')};
  letter-spacing: ${({ size }) =>
    size === 'small'
      ? theme.typography.body.small.button.letterSpacing
      : theme.typography.body.medium.button.letterSpacing};
  background-color: ${theme.colors.backgroundCover.default};
  &:hover {
    background-color: ${theme.colors.backgroundCover.hover};
  }

  ${({ disabled }) =>
    disabled &&
    `
    background-color: ${theme.colors.backgroundCover.disabled};
    cursor: not-allowed;
    &:hover {
      background-color: ${theme.colors.backgroundCover.disabled};
    }
  `}

  color: ${({ color, disabled }) => {
    if (disabled) {
      return theme.colors.textInverted.disabled;
    } else {
      if (color === 'highlighted') {
        return theme.colors.actionPrimary.default;
      } else if (color === 'danger') {
        return theme.colors.emotionDanger.default;
      } else {
        return theme.colors.text.primary;
      }
    }
  }};

  svg {
    width: ${({ size }) => (size === 'small' ? '16px' : '22px')};
    height: ${({ size }) => (size === 'small' ? '16px' : '22px')};
  }

  svg path {
    fill: ${({ color, disabled }) => {
      if (disabled) {
        return theme.colors.iconText.disabled;
      } else {
        if (color === 'highlighted') {
          return theme.colors.iconAction.primary;
        } else if (color === 'danger') {
          return theme.colors.emotionDanger.default;
        } else {
          return theme.colors.iconText.primary;
        }
      }
    }};
  }

  padding: ${({ size }) => {
    return size === 'small' ? `7px` : `7px 11px`;
  }};

  &:has(svg) {
    padding: 7px;
  }
`;
